import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { environment } from "src/environments/environment";
import {
    AssessmentWorkItemApiError, isWorkItemStatusSelector, requireWorkItemApiData,
    requireWorkItemAssessmentOptions, requireWorkItemAssigneeOptions, requireWorkItemCount,
    requireWorkItemEvidenceDetail, requireWorkItemPage, requireWorkItemStatusOptions
} from "./assessment_work_items.dto";
import { workItemStatusSelector } from "./assessment_work_items.types";
import type {
    AssessmentWorkItem, AssessmentWorkItemAssessmentOption, AssessmentWorkItemAssigneeOption,
    AssessmentWorkItemCount, AssessmentWorkItemEvidenceDetail, AssessmentWorkItemListFilters,
    AssessmentWorkItemOffsetPage, AssessmentWorkItemOptionFilters, AssessmentWorkItemPage,
    AssessmentWorkItemStatusOption
} from "./assessment_work_items.types";

const MAX_LIST_PAGE_SIZE = 100;
const DEFAULT_LIST_PAGE_SIZE = 25;
const MAX_LIST_OFFSET = 10000;
const MAX_OPTION_LIMIT = 100;
const DEFAULT_OPTION_LIMIT = 25;
const MAX_OPTION_OFFSET = 10000;
const MAX_FILTER_IDS = 100;
const MAX_STATUS_SELECTORS = 8;
const MAX_LIST_SEARCH_LENGTH = 200;
const MAX_OPTION_SEARCH_LENGTH = 100;
const MAX_KEY_LENGTH = 510;

const LIST_FIELDS = new Set([
    "page", "pageSize", "search", "assignedToMe", "assigneeUserIds", "assessmentIds",
    "statuses", "itemType", "view", "requirement"
]);
const OPTION_FIELDS = new Set([
    "offset", "limit", "search", "itemSearch", "assignedToMe", "assigneeUserIds",
    "assessmentIds", "statuses", "itemType", "view", "requirement"
]);

// The response must be checked against the exact filters used to build its request.
function snapshotFilters<T extends AssessmentWorkItemListFilters | AssessmentWorkItemOptionFilters>(filters: T): T {
    if (!filters || typeof filters !== "object" || Array.isArray(filters)) invalidFilter("filters");
    return {
        ...filters,
        assigneeUserIds: filters.assigneeUserIds?.slice(),
        assessmentIds: filters.assessmentIds?.slice(),
        statuses: filters.statuses?.slice(),
        requirement: filters.requirement ? { ...filters.requirement } : undefined
    } as T;
}

function invalidFilter(name: string): never {
    throw new AssessmentWorkItemApiError(`Invalid ${name} work-item filter.`);
}

function requireFilters(value: object, allowed: ReadonlySet<string>): void {
    if (!value || typeof value !== "object" || Array.isArray(value) ||
        Object.keys(value).some((field) => !allowed.has(field))) {
        invalidFilter("unknown");
    }
}

function positiveInteger(value: number, name: string, maximum = Number.MAX_SAFE_INTEGER): number {
    if (!Number.isSafeInteger(value) || value < 1 || value > maximum) invalidFilter(name);
    return value;
}

function nonnegativeInteger(value: number, name: string, maximum: number): number {
    if (!Number.isSafeInteger(value) || value < 0 || value > maximum) invalidFilter(name);
    return value;
}

function boundedText(value: string, name: string, maximum: number): string {
    if (typeof value !== "string" || value.length > maximum ||
        /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/.test(value)) invalidFilter(name);
    return value;
}

function idList(value: readonly number[] | undefined, name: string): string | null {
    if (value === undefined) return null;
    if (!Array.isArray(value) || value.length > MAX_FILTER_IDS) invalidFilter(name);
    const ids = value.map((id) => positiveInteger(id, name));
    if (new Set(ids).size !== ids.length) invalidFilter(name);
    return ids.length ? ids.join(",") : null;
}

function statusList(value: AssessmentWorkItemListFilters["statuses"]): string | null {
    if (value === undefined) return null;
    if (!Array.isArray(value) || value.length > MAX_STATUS_SELECTORS ||
        value.some((selector) => !isWorkItemStatusSelector(selector)) ||
        new Set(value).size !== value.length) invalidFilter("statuses");
    return value.length ? value.join(",") : null;
}

function commonParams(filters: AssessmentWorkItemListFilters | AssessmentWorkItemOptionFilters): HttpParams {
    let params = new HttpParams();
    if (filters.assignedToMe !== undefined) {
        if (typeof filters.assignedToMe !== "boolean") invalidFilter("assignedToMe");
        params = params.set("assignedToMe", String(filters.assignedToMe));
    }
    for (const name of ["assigneeUserIds", "assessmentIds"] as const) {
        const ids = idList(filters[name], name);
        if (ids !== null) params = params.set(name, ids);
    }
    const statuses = statusList(filters.statuses);
    if (statuses !== null) params = params.set("statuses", statuses);
    if (filters.itemType !== undefined) {
        if (filters.itemType !== "all" && filters.itemType !== "task" && filters.itemType !== "evidence") {
            invalidFilter("itemType");
        }
        params = params.set("itemType", filters.itemType);
    }
    if (filters.view !== undefined) {
        if (filters.view !== "header" && filters.view !== "assessment_footer" &&
            filters.view !== "requirement_footer") invalidFilter("view");
        params = params.set("view", filters.view);
    }
    const assessmentCount = filters.assessmentIds?.length ?? 0;
    if (filters.view !== undefined && filters.view !== "header" && assessmentCount !== 1) {
        invalidFilter("assessmentIds");
    }
    if (filters.requirement !== undefined) {
        if (assessmentCount !== 1 || !filters.requirement ||
            typeof filters.requirement !== "object" ||
            Object.keys(filters.requirement).some((key) => key !== "sectionKey" && key !== "requirementKey")) {
            invalidFilter("requirement");
        }
        for (const name of ["sectionKey", "requirementKey"] as const) {
            const value = filters.requirement[name];
            if (typeof value !== "string" || !value || value.trim() !== value ||
                value.length > MAX_KEY_LENGTH || /[\x00-\x1f\x7f]/.test(value)) invalidFilter(name);
            params = params.set(name, value);
        }
    }
    if (filters.view === "assessment_footer" && filters.requirement !== undefined) invalidFilter("requirement");
    if (filters.view === "requirement_footer" && filters.requirement === undefined) invalidFilter("requirement");
    return params;
}

function listParams(filters: AssessmentWorkItemListFilters): HttpParams {
    requireFilters(filters, LIST_FIELDS);
    const page = positiveInteger(filters.page ?? 1, "page");
    const pageSize = positiveInteger(filters.pageSize ?? DEFAULT_LIST_PAGE_SIZE, "pageSize", MAX_LIST_PAGE_SIZE);
    if ((page - 1) * pageSize > MAX_LIST_OFFSET) invalidFilter("page");
    let params = commonParams(filters);
    if (filters.page !== undefined) params = params.set("page", String(page));
    if (filters.pageSize !== undefined) params = params.set("pageSize", String(pageSize));
    if (filters.search !== undefined) {
        params = params.set("search", boundedText(filters.search, "search", MAX_LIST_SEARCH_LENGTH));
    }
    return params;
}

function optionParams(filters: AssessmentWorkItemOptionFilters): HttpParams {
    requireFilters(filters, OPTION_FIELDS);
    const offset = nonnegativeInteger(filters.offset ?? 0, "offset", MAX_OPTION_OFFSET);
    const limit = positiveInteger(filters.limit ?? DEFAULT_OPTION_LIMIT, "limit", MAX_OPTION_LIMIT);
    let params = commonParams(filters);
    if (filters.offset !== undefined) params = params.set("offset", String(offset));
    if (filters.limit !== undefined) params = params.set("limit", String(limit));
    if (filters.search !== undefined) {
        params = params.set("search", boundedText(filters.search, "search", MAX_OPTION_SEARCH_LENGTH));
    }
    if (filters.itemSearch !== undefined) {
        params = params.set("itemSearch", boundedText(filters.itemSearch, "itemSearch", MAX_LIST_SEARCH_LENGTH));
    }
    return params;
}

function matchesRequestedScope(item: AssessmentWorkItem, filters: AssessmentWorkItemListFilters): boolean {
    const source = item.kind === "task" ? item.task : item.evidence;
    if (filters.assessmentIds?.length && !filters.assessmentIds.includes(source.productLicenseId)) return false;
    if (filters.itemType && filters.itemType !== "all" && item.kind !== filters.itemType) return false;
    if (filters.statuses?.length && !filters.statuses.includes(workItemStatusSelector(item))) return false;
    return !filters.requirement || source.requirements.some((requirement) =>
        requirement.sectionKey === filters.requirement?.sectionKey &&
        requirement.requirementKey === filters.requirement?.requirementKey);
}

@Injectable({ providedIn: "root" })
export class AssessmentWorkItemsService {
    private readonly endpoint = `${environment.baseurl.replace(/\/$/, "")}/assessment-work-items`;

    constructor(private readonly http: HttpClient) {}

    listWorkItems(filters: AssessmentWorkItemListFilters = {}): Observable<AssessmentWorkItemPage> {
        const requested = snapshotFilters(filters);
        const params = listParams(requested);
        return this.get(this.endpoint, (data) => {
            const page = requireWorkItemPage(data);
            if (page.page !== (requested.page ?? 1) ||
                page.pageSize !== (requested.pageSize ?? DEFAULT_LIST_PAGE_SIZE) ||
                page.items.some((item) => !matchesRequestedScope(item, requested))) {
                throw new AssessmentWorkItemApiError("Work-item list response changed scope.");
            }
            return page;
        }, params);
    }

    getAssessmentFilterOptions(filters: AssessmentWorkItemOptionFilters = {}):
        Observable<AssessmentWorkItemOffsetPage<AssessmentWorkItemAssessmentOption>> {
        const requested = snapshotFilters(filters);
        const params = optionParams(requested);
        return this.get(`${this.endpoint}/filter-options/assessments`, (data) => {
            const page = requireWorkItemAssessmentOptions(data);
            if (!this.matchesOptionPage(page, requested) ||
                page.items.some((option) => requested.assessmentIds?.length &&
                    !requested.assessmentIds.includes(option.productLicenseId))) {
                throw new AssessmentWorkItemApiError("Assessment options response changed scope.");
            }
            return page;
        }, params);
    }

    getAssigneeFilterOptions(filters: AssessmentWorkItemOptionFilters = {}):
        Observable<AssessmentWorkItemOffsetPage<AssessmentWorkItemAssigneeOption>> {
        const requested = snapshotFilters(filters);
        const params = optionParams(requested);
        return this.get(`${this.endpoint}/filter-options/assignees`, (data) => {
            const page = requireWorkItemAssigneeOptions(data);
            if (!this.matchesOptionPage(page, requested)) {
                throw new AssessmentWorkItemApiError("Assignee options response changed page.");
            }
            return page;
        }, params);
    }

    getStatusFilterOptions(filters: AssessmentWorkItemOptionFilters = {}):
        Observable<AssessmentWorkItemOffsetPage<AssessmentWorkItemStatusOption>> {
        const requested = snapshotFilters(filters);
        const params = optionParams(requested);
        return this.get(`${this.endpoint}/filter-options/statuses`, (data) => {
            const page = requireWorkItemStatusOptions(data);
            if (!this.matchesOptionPage(page, requested) || page.items.some((option) =>
                (requested.statuses?.length && !requested.statuses.includes(option.value)) ||
                (requested.itemType && requested.itemType !== "all" &&
                    !option.value.startsWith(`${requested.itemType}:`)))) {
                throw new AssessmentWorkItemApiError("Status options response changed scope.");
            }
            return page;
        }, params);
    }

    getMyUnfinishedCount(): Observable<AssessmentWorkItemCount> {
        return this.get(`${this.endpoint}/counts/me/unfinished`, requireWorkItemCount);
    }

    getEvidenceDetail(assessmentId: number, evidenceId: number): Observable<AssessmentWorkItemEvidenceDetail> {
        const licenseId = positiveInteger(assessmentId, "assessment ID");
        const itemId = positiveInteger(evidenceId, "evidence ID");
        return this.get(`${this.endpoint}/assessments/${licenseId}/evidence/${itemId}`,
            (data) => requireWorkItemEvidenceDetail(data, licenseId, itemId));
    }

    private matchesOptionPage<T>(page: AssessmentWorkItemOffsetPage<T>,
        filters: AssessmentWorkItemOptionFilters): boolean {
        return page.offset === (filters.offset ?? 0) && page.limit === (filters.limit ?? DEFAULT_OPTION_LIMIT);
    }

    private get<T>(url: string, normalize: (data: unknown) => T, params?: HttpParams): Observable<T> {
        return this.http.get<unknown>(url, { params }).pipe(
            map((response) => normalize(requireWorkItemApiData(response, "Unable to load assessment work items.")))
        );
    }
}
